import { useEffect, useState } from 'react';

import { Autocomplete, Paper, Skeleton, TextField } from '@mui/material';
import type {
  AutocompleteProps,
  AutocompleteRenderInputParams,
  PaperProps,
} from '@mui/material';
import type { IntrospectionType } from 'graphql';
import { Search } from 'lucide-react';

import type { Order_By } from '@graphql/__generated__/graphql';
import type {
  AddWhereClause,
  DeleteWhereClause,
} from '@modules/_shared/components/filters/Filters';
import { ActivityStatusFilter } from '@modules/_shared/components/filters/quick-filters/ActivityStatusFilter';
import {
  BooleanFilter,
  type NullCheckMode,
} from '@modules/_shared/components/filters/quick-filters/BooleanFilter';
import { DateFilter } from '@modules/_shared/components/filters/quick-filters/DateFilter';
import type { EnumFilterProps } from '@modules/_shared/components/filters/quick-filters/EnumFilter';
import { EnumFilter } from '@modules/_shared/components/filters/quick-filters/EnumFilter';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import { FsboBlockedAdvertiserFilter } from '@modules/_shared/components/filters/quick-filters/FsboBlockedAdvertiserFilter';
import { ListingFilter } from '@modules/_shared/components/filters/quick-filters/ListingFilter';
import { MlsAccessFilter } from '@modules/_shared/components/filters/quick-filters/MlsAccessFilter';
import { MlsBrokerFilter } from '@modules/_shared/components/filters/quick-filters/MlsBrokerFilter';
import { MlsNetworkTeamFilter } from '@modules/_shared/components/filters/quick-filters/MlsNetworkTeamFilter';
import { MlsTeamFilter } from '@modules/_shared/components/filters/quick-filters/MlsTeamFilter';
import { NumberFilter } from '@modules/_shared/components/filters/quick-filters/NumberFilter';
import { PlaceFilter } from '@modules/_shared/components/filters/quick-filters/PlaceFilter';
import { StringFilter } from '@modules/_shared/components/filters/quick-filters/StringFilter';
import { TeamFilter } from '@modules/_shared/components/filters/quick-filters/TeamFilter';
import { UserFilter } from '@modules/_shared/components/filters/quick-filters/UserFilter';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import { useLocale } from '@shared/hooks/locale';

export const dropdownShadow =
  '0 2px 4px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.08)';

export const searchFieldSx = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'grey.300',
  },
  '& .MuiOutlinedInput-root': {
    gap: '6px',
  },
};

export const filterPopperProps = {
  // forcePopupIcon: false fully removes the popupIndicator IconButton AND
  // the .MuiAutocomplete-hasPopupIcon class — without it, the input root
  // still reserves ~65px of padding-right for an (empty) icon button, which
  // pushes the loading spinner ~65px inward from the right edge.
  forcePopupIcon: false as const,
  slotProps: {
    popper: { sx: { width: '100% !important' } },
  },
};

const TextFieldPaper = (props: PaperProps) => {
  return (
    <Paper
      {...props}
      elevation={0}
      sx={{
        p: 1,
        border: '1px solid',
        borderColor: 'grey.300',
        borderBottom: 'none',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        boxShadow: dropdownShadow,
        clipPath: 'inset(-20px -20px 0px -20px)',
      }}
    />
  );
};

export const AutocompleteTextField = (props: AutocompleteRenderInputParams) => {
  const { t } = useLocale();
  const { slotProps, ...textFieldProps } = props;

  return (
    <TextFieldPaper>
      <TextField
        {...textFieldProps}
        placeholder={t('Search')}
        autoFocus
        variant="outlined"
        size="small"
        sx={searchFieldSx}
        slotProps={{
          ...slotProps,
          input: {
            ...slotProps.input,
            startAdornment: (
              <>
                <Search size={16} />
                {slotProps.input.startAdornment}
              </>
            ),
          },
          htmlInput: {
            ...slotProps.htmlInput,
            autoComplete: 'one-time-code', // disable autocomplete and autofill
          },
        }}
      />
    </TextFieldPaper>
  );
};

export const AutocompletePaper = (props: PaperProps) => {
  const { sx: _sx, ...rest } = props;
  return (
    <Paper
      {...rest}
      elevation={0}
      sx={{
        mt: 0,
        borderTop: '0 !important',
        borderLeft: '1px solid',
        borderRight: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'grey.300',
        borderRadius: '0 0 8px 8px !important',
        boxShadow: dropdownShadow,
        '& .MuiAutocomplete-noOptions': {
          py: 1,
        },
      }}
    />
  );
};

export type AutocompleteItem = {
  value: string | number | boolean;
  label: string;
};

export const useFilterAutocompleteOpen = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(true);
  }, []);

  return open;
};

export const FilterAutocomplete = <T extends AutocompleteItem>(
  props: AutocompleteProps<T, true, true, false>,
) => {
  const open = useFilterAutocompleteOpen();

  return (
    <Autocomplete
      {...props}
      open={open}
      openOnFocus
      disablePortal
      disableCloseOnSelect
      multiple
      size="small"
      clearIcon={null}
      {...filterPopperProps}
      sx={{ minWidth: 150 }}
      isOptionEqualToValue={
        props.isOptionEqualToValue ??
        ((option, value) => option.value === value.value)
      }
      renderOption={
        props.renderOption ??
        ((props, option, { index }) => (
          <li {...props} key={`option-${option.value}-${index}`}>
            {option.label}
          </li>
        ))
      }
      slots={{
        paper: AutocompletePaper,
      }}
    />
  );
};

export type filterAutocompleteRenderOptionProps = NonNullable<
  AutocompleteProps<any, true, true, false>['renderOption']
>;

export type DateFilterField = {
  value: string;
  label: string;
};

export interface QuickFiltersProps {
  path: string[];
  label: string;
  displayedColumn?: string;
  disabled?: (where: {}) => boolean;
  filter?: {
    path: string[];
    value: Parameters<AddWhereClause>[2];
  };
  onChange?: (value: any, whereClause: {}) => void | {};
  addWhereClause: AddWhereClause;
  deleteWhereClause: DeleteWhereClause;
  queryParamsScope?: string;
  orderBy?: Record<string, Order_By> | Array<Record<string, Order_By>>;
  filterAutocompleteRenderOption?: filterAutocompleteRenderOptionProps;
  isNullable?: boolean;
  isScraperSchema?: boolean;
  includeParentsPlaces?: boolean;
  expandSelectionAncestors?: boolean;
  excludeMode?: boolean;
  enumValueLabel?: (value: string) => string;
  nullCheckMode?: NullCheckMode;
  // 2+ entries: DateFilter shows a dropdown to pick which date column the
  // range targets (the value replaces the last segment of `path`).
  dateFields?: DateFilterField[];
}

export interface QuickFilterProps extends QuickFiltersProps {
  getValueFromPath: (path: string[], where: any) => any;
  getTypeFromPath: (path: string[]) => IntrospectionType | undefined;
  where: any;
  type: IntrospectionType;
}

export const QuickFilter = (props: QuickFiltersProps) => {
  const {
    path,
    label,
    queryParamsScope,
    isNullable,
    includeParentsPlaces,
    expandSelectionAncestors,
    nullCheckMode,
  } = props;

  const {
    getTypeFromPath,
    getValueFromPath,
    isNullable: isSchemaNullable,
  } = useSchema();
  const [where] = useFiltersSearchParams(queryParamsScope);
  const [, ...wherePath] = path;
  const defaultValue = wherePath.reduce((p, c) => (p && p[c]) || null, where);

  if (path[0] === 'activities_bool_exp' && path[1] === 'activity_status') {
    return (
      <ActivityStatusFilter
        label={label}
        where={where}
        queryParamsScope={queryParamsScope}
        disabled={props.disabled}
      />
    );
  }

  const type = getTypeFromPath(path);
  if (!type) {
    // Schema still loading
    return <Skeleton variant="rounded" height={32} width={120} />;
  }

  const baseProps: QuickFilterProps & { defaultValue: any } = {
    ...props,
    nullCheckMode,
    getValueFromPath,
    getTypeFromPath,
    defaultValue,
    type,
    where,
    isNullable:
      typeof isNullable === 'undefined' ? isSchemaNullable(path) : isNullable,
  };

  // Special case: MLS access_status computed field
  if (path.includes('access_status') && path[0] === 'mls_lots_bool_exp') {
    return <MlsAccessFilter {...baseProps} />;
  }

  // Special case: "has a blocked FSBO advertiser" goes through the `sellers`
  // array relationship, so its "No" must be a negated EXISTS.
  if (
    path[0] === 'lots_bool_exp' &&
    path[path.length - 1] === 'fsbo_blocked_at'
  ) {
    return <FsboBlockedAdvertiserFilter {...baseProps} />;
  }

  if (type.kind === 'INPUT_OBJECT' && nullCheckMode != null) {
    return <BooleanFilter {...baseProps} />;
  }

  if (type.kind === 'INPUT_OBJECT' && type.name === 'Boolean_comparison_exp') {
    return <BooleanFilter {...baseProps} />;
  }
  if (
    type.kind === 'INPUT_OBJECT' &&
    ['timestamptz_comparison_exp', 'date_comparison_exp'].includes(type.name)
  ) {
    return <DateFilter {...baseProps} />;
  }
  if (
    type.kind === 'INPUT_OBJECT' &&
    [
      'float8_comparison_exp',
      'Int_comparison_exp',
      'Float_comparison_exp',
    ].includes(type.name)
  ) {
    return <NumberFilter {...baseProps} />;
  }
  // For scrapers schema it's called {ch,fr,es,it}_places_bool_exp
  if (type.kind === 'INPUT_OBJECT' && type.name.includes('places_bool_exp')) {
    return (
      <PlaceFilter
        {...baseProps}
        includeParentsPlaces={includeParentsPlaces}
        expandSelectionAncestors={expandSelectionAncestors}
      />
    );
  }
  if (type.kind === 'INPUT_OBJECT' && type.name === 'users_bool_exp') {
    return <UserFilter {...baseProps} />;
  }
  // mls_brokers is a cross-tenant view - use specialized filter that queries via mls_lots
  if (type.kind === 'INPUT_OBJECT' && type.name === 'mls_brokers_bool_exp') {
    return <MlsBrokerFilter {...baseProps} />;
  }
  // mls_teams is a cross-tenant view - use specialized filter
  if (type.kind === 'INPUT_OBJECT' && type.name === 'mls_teams_bool_exp') {
    return <MlsTeamFilter {...baseProps} />;
  }
  // mls_network_teams exposes cross-tenant data — use a dedicated filter that queries
  // mls_network_teams for options (consistent with the table) and writes to id._in directly
  // to avoid going through the RLS-filtered `teams` relationship.
  if (path[0] === 'mls_network_teams_bool_exp' && path[1] === 'id') {
    return <MlsNetworkTeamFilter {...baseProps} />;
  }
  if (type.kind === 'INPUT_OBJECT' && type.name === 'teams_bool_exp') {
    return <TeamFilter {...baseProps} />;
  }
  if (type.kind === 'INPUT_OBJECT' && type.name === 'lots_bool_exp') {
    return <ListingFilter {...baseProps} />;
  }
  if (type.kind === 'SCALAR') {
    return <StringFilter {...baseProps} />;
  }
  if (type.kind === 'ENUM') {
    return <EnumFilter {...(baseProps as EnumFilterProps)} />;
  }
  return (
    <FilterChip
      label={label}
      renderFilter={() => (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            maxWidth: 500,
            maxHeight: 500,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            rowGap: 1,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            boxShadow: dropdownShadow,
          }}
        >
          <pre>{JSON.stringify({ path, type }, null, 2)}</pre>
        </Paper>
      )}
    />
  );
};
